// @ts-check

/** @file HTTP transport used by tenant-bound Velocious SyncClient instances. */

import {APP_FRONTEND_REQUEST_HEADER, APP_FRONTEND_REQUEST_VALUE} from "../../../shared/app-frontend-request.js"
import {resolveApiBaseUrl} from "../shared/backend-api.js"
import {getSessionTokenSync, getSessionUserIdSync, SESSION_TOKEN_HEADER} from "../shared/session-option.js"
import {projectSyncConnectivity} from "../sync/project-sync-connectivity.js"
import {parseProjectSyncHttpResponse} from "../sync/project-sync-http-response.js"
import {ProjectSyncTransportError} from "../sync/project-sync-error.js"
import {projectReplicaPendingMutationCount} from "./project-replica-runtime.js"

/** Public SyncClient transport; protocol routing fields are captured by each tenant client. */
export const projectReplicaTransport = {
  /**
   * Sends one authenticated project-bound sync request.
   * @param {string} path Sync mount-relative path.
   * @param {Record<string, unknown>} body Captured SyncClient request body.
   * @param {{signal?: AbortSignal}} [options] Optional cancellation state.
   * @returns {Promise<{json: () => unknown}>} Velocious transport response.
   * @rejects {Error} When session, identity, routing, or HTTP validation fails.
   */
  async post(path, body, options = {}) {
    const userId = getSessionUserIdSync()
    if (!body || typeof body !== "object" || Array.isArray(body)) throw new Error("Project sync requires an object request body")
    const requestBody = {...body}
    if (!userId) throw new ProjectSyncTransportError({code: "authentication-required", retryable: false})
    requestBody.pendingMutationCount = await projectReplicaPendingMutationCount({projectId: requestBody.projectId, userId})

    return await authenticatedSyncPost(path, requestBody, options)
  }
}

/**
 * Sends one authenticated sync request without assuming project routing.
 * @param {string} path Absolute API path.
 * @param {Record<string, unknown>} body Request JSON.
 * @param {{reportConnectivity?: boolean, signal?: AbortSignal}} [options] Cancellation and connectivity-report policy.
 * @returns {Promise<{json: () => unknown}>} Validated response facade.
 * @rejects {Error} When authentication, transport, HTTP, or protocol validation fails.
 */
export async function authenticatedSyncPost(path, body, options = {}) {
  // `reportConnectivity: false` is for auth bootstrap calls (offline-grant):
  // their success does not prove the sync transport works, and flipping the
  // connectivity gate re-arms coordinator cycles that were deliberately held
  // offline. Only real sync transport requests may mark the gate online.
  const reportConnectivity = options.reportConnectivity !== false
  const token = getSessionTokenSync()
  if (!token) throw new ProjectSyncTransportError({code: "authentication-required", retryable: false})
  const routeParams = typeof window === "undefined" || !window.location
    ? {}
    : Object.fromEntries(new URLSearchParams(window.location.search).entries())
  let response

  try {
    response = await fetch(`${resolveApiBaseUrl(routeParams)}${path}`, {
      body: JSON.stringify(body),
      headers: {
        "content-type": "application/json",
        [APP_FRONTEND_REQUEST_HEADER]: APP_FRONTEND_REQUEST_VALUE,
        [SESSION_TOKEN_HEADER]: token
      },
      method: "POST",
      signal: options.signal
    })
  } catch (error) {
    if (options.signal?.aborted) throw error
    if (reportConnectivity) projectSyncConnectivity.requestFailed()
    throw new ProjectSyncTransportError({code: "network-unavailable", retryable: true})
  }

  const payload = await parseProjectSyncHttpResponse(response).catch((error) => {
    // Headers arrived but the body did not: that is the same transport signal
    // as a failed request, so the connectivity gate closes for it too.
    if (reportConnectivity && error instanceof ProjectSyncTransportError && error.code === "network-unavailable") {
      projectSyncConnectivity.requestFailed()
    }
    throw error
  })

  if (reportConnectivity) projectSyncConnectivity.requestSucceeded()
  return {json: () => payload}
}
